import { AppStorage, ServiceClient } from 'src/utils'
import { ServiceError } from 'src/utils/ServiceError'
import { Lote, LoteDetalhe, Parceiro, PreviaCnpjs } from './CadastroLote.scopes'

const serviceClient = ServiceClient.singleton()
const appStorage = AppStorage.singleton()

const BASE_URL = '/api/cadastro-lote'

type LoteApiResponse = {
    id: string
    ownerId: string
    ownerNome: string
    ownerEmail: string
    solicitante?: string
    status: Lote['status']
    total: number
    processados: number
    cadastrados: number
    erro?: string
    createdAt: string
    startedAt?: string
    finishedAt?: string
}

type LoteDetalheApiResponse = LoteApiResponse & {
    itens: {
        seq: number
        cnpj: string
        status: string
        razaoSocial?: string
        dealId?: string
        mensagem?: string
        processedAt?: string
    }[]
    resumo: { status: string; qtde: number }[]
}

const toDate = (value?: string) => (value ? new Date(value) : undefined)

function mapLote(item: LoteApiResponse): Lote {
    return {
        id: item.id,
        ownerId: item.ownerId,
        ownerNome: item.ownerNome,
        ownerEmail: item.ownerEmail,
        solicitante: item.solicitante,
        status: item.status,
        total: item.total,
        processados: item.processados,
        cadastrados: item.cadastrados,
        erro: item.erro,
        createdAt: new Date(item.createdAt),
        startedAt: toDate(item.startedAt),
        finishedAt: toDate(item.finishedAt)
    }
}

/**
 * Valida o dígito verificador do CNPJ. Mesma regra do servidor (pj-utils), replicada aqui
 * para a conferência prévia acontecer sem ida ao backend.
 */
export function isValidCNPJ(entrada: string): boolean {
    const cnpj = (entrada || '').replace(/\D/g, '')
    if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return false

    const digitoVerificador = (tamanho: number) => {
        let soma = 0
        let peso = tamanho - 7
        for (let i = tamanho; i >= 1; i--) {
            soma += parseInt(cnpj.charAt(tamanho - i), 10) * peso--
            if (peso < 2) peso = 9
        }
        return soma % 11 < 2 ? 0 : 11 - (soma % 11)
    }

    return (
        digitoVerificador(12) === parseInt(cnpj.charAt(12), 10) &&
        digitoVerificador(13) === parseInt(cnpj.charAt(13), 10)
    )
}

export function formatCNPJ(entrada: string): string {
    const cnpj = (entrada || '').replace(/\D/g, '')
    if (cnpj.length !== 14) return entrada
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`
}

/** Separa o texto colado em CNPJs válidos (sem repetição), inválidos e duplicados. */
export function conferirCnpjs(texto: string): PreviaCnpjs {
    const validos: string[] = []
    const invalidos: string[] = []
    const duplicados: string[] = []
    const vistos = new Set<string>()

    for (const bruto of (texto || '').split(/[\s,;]+/)) {
        const linha = bruto.trim()
        if (!linha) continue

        const cnpj = linha.replace(/\D/g, '')
        if (!isValidCNPJ(cnpj)) {
            invalidos.push(linha)
            continue
        }
        if (vistos.has(cnpj)) {
            duplicados.push(cnpj)
            continue
        }
        vistos.add(cnpj)
        validos.push(cnpj)
    }

    return { validos, invalidos, duplicados }
}

export class CadastroLoteService {
    static #INSTANCE = new CadastroLoteService()
    static readonly singleton = () => CadastroLoteService.#INSTANCE

    async listarParceiros(busca?: string): Promise<Parceiro[]> {
        const params = busca ? `?q=${encodeURIComponent(busca)}` : ''
        return serviceClient.authGet<Parceiro[]>(`${BASE_URL}/parceiros${params}`)
    }

    async criarLote(ownerId: string, cnpjs: string[]): Promise<Lote> {
        const response = await serviceClient.authPost<LoteApiResponse>(BASE_URL, { ownerId, cnpjs })
        return mapLote(response)
    }

    async obterLote(loteId: string): Promise<LoteDetalhe> {
        const response = await serviceClient.authGet<LoteDetalheApiResponse>(`${BASE_URL}/${loteId}`)
        return {
            ...mapLote(response),
            resumo: response.resumo,
            itens: response.itens.map((item) => ({
                seq: item.seq,
                cnpj: item.cnpj,
                status: item.status,
                razaoSocial: item.razaoSocial,
                dealId: item.dealId,
                mensagem: item.mensagem,
                processedAt: toDate(item.processedAt)
            }))
        }
    }

    async listarHistorico(limit = 30): Promise<Lote[]> {
        const response = await serviceClient.authGet<LoteApiResponse[]>(`${BASE_URL}?limit=${limit}`)
        return response.map(mapLote)
    }

    async cancelarLote(loteId: string): Promise<void> {
        await serviceClient.authPost(`${BASE_URL}/${loteId}/cancelar`)
    }

    /**
     * O CSV vem de uma rota autenticada, então não dá para usar um <a href>: baixa por fetch
     * e entrega o conteúdo como blob.
     */
    async baixarCsv(loteId: string): Promise<{ nome: string; blob: Blob }> {
        const accessToken = appStorage.accessToken
        if (!accessToken) {
            throw new ServiceError(401, 'Unauthorized')
        }

        const response = await fetch(`${BASE_URL}/${loteId}/csv`, {
            method: 'GET',
            headers: { Authorization: `BEARER ${accessToken}` }
        })

        if (!response.ok) {
            throw new ServiceError(response.status, response.statusText)
        }

        return {
            nome: `cadastro-lote-${loteId}.csv`,
            blob: await response.blob()
        }
    }
}

export default CadastroLoteService.singleton()
